import React from "react";
import { Link } from "react-router-dom";
import Humanizer from "@/components/Humanizer";
import { useAuth } from "@/context/AuthContext";
import { GraduationCap, Megaphone, Briefcase, PenLine, Users, Sparkles, ArrowRight, Shield, Zap, FileText, Chrome } from "lucide-react";

const FAQ = [
  { q: "Does Humanly detect AI text?", a: "No. Humanly rewrites AI-generated writing so it reads naturally. We focus on producing text that reads like a real human wrote it — not on detection." },
  { q: "Will my meaning be preserved?", a: "Yes. Every rewrite preserves your intended meaning while adjusting rhythm, cadence, and word choice. You can dial the strength up or down." },
  { q: "Is my writing kept private?", a: "Your text is stored securely against your account and used only for your history. We never train models on your content." },
  { q: "Can Humanly match my own voice?", a: "Yes — create a Personal Style Profile by pasting a paragraph you wrote yourself, and every rewrite will emulate your rhythm, vocabulary, and quirks." },
];

const AUDIENCES = [
  { icon: GraduationCap, title: "Students", desc: "Turn stiff drafts into essays that sound like you — clearer, warmer, and yours." },
  { icon: Megaphone, title: "Marketers", desc: "Rewrite AI copy into campaigns that feel like a person, not a template." },
  { icon: PenLine, title: "Creators", desc: "Add voice and rhythm to newsletters, scripts, blog posts, and social captions." },
  { icon: Briefcase, title: "Professionals", desc: "Emails, memos, and reports that read polished and unmistakably human." },
  { icon: Users, title: "Teams", desc: "Consistent brand voice across everyone — driven by shared style profiles." },
  { icon: FileText, title: "Everyday writers", desc: "Any AI paragraph, one click, more like you." },
];

const HOW = [
  { n: "01", title: "Paste your AI text", desc: "Drop in a paragraph, an essay, or a full document. PDF, DOCX, and TXT supported." },
  { n: "02", title: "Tune the controls", desc: "Pick tone, audience, formality, rewrite strength, and length — or use smart defaults." },
  { n: "03", title: "Read it side-by-side", desc: "Compare original and humanized, tweak, then copy in one tap." },
];

export default function Home() {
  const { user } = useAuth();
  return (
    <div>
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-14 pb-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
            <div className="lg:col-span-7 fade-up">
              <div className="pill mb-6" data-testid="hero-eyebrow">
                <Sparkles size={12} className="text-[hsl(var(--accent))]" />
                <span>Rewrites that read like a real person wrote them</span>
              </div>
              <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl leading-[1.02] tracking-tight text-[hsl(var(--foreground))]">
                Make AI writing<br/>
                <span className="italic">sound like <span className="text-[hsl(var(--accent))]">you.</span></span>
              </h1>
              <p className="mt-6 text-lg text-[hsl(var(--muted-foreground))] max-w-xl leading-relaxed">
                Humanly turns robotic AI drafts into natural, human prose — with tone controls, personal style profiles, and side-by-side comparison. No detection games. Just better writing.
              </p>
              <div className="mt-8 flex flex-wrap items-center gap-4">
                <Link to="/signup" data-testid="hero-cta-primary" className="btn-primary flex items-center gap-2">
                  Start free <ArrowRight size={16} />
                </Link>
                <a href="#humanizer" data-testid="hero-cta-secondary" className="btn-ghost text-sm">Try it now — no signup</a>
              </div>
              <div className="mt-8 flex items-center gap-6 text-sm text-[hsl(var(--muted-foreground))]">
                <div className="flex items-center gap-2"><Shield size={14} /> Private & secure</div>
                <div className="flex items-center gap-2"><Zap size={14} /> Frontier-model powered</div>
                <a
                  href="/humanly-extension.zip"
                  data-testid="hero-extension-link"
                  className="flex items-center gap-2 link-underline text-[hsl(var(--primary))] font-semibold"
                >
                  <Chrome size={14} /> Chrome extension
                </a>
              </div>
            </div>
            <div className="lg:col-span-5 relative fade-up delay-2">
              <div className="relative rounded-2xl overflow-hidden border border-[hsl(var(--border))] shadow-[0_20px_60px_rgba(44,76,59,0.16)]">
                <img
                  src="https://images.unsplash.com/photo-1596486935250-3ca1fb5c45b8?crop=entropy&cs=srgb&fm=jpg&q=85"
                  alt="warm textured background"
                  className="w-full h-[380px] object-cover"
                />
                <div className="absolute inset-0 hero-grain" />
                <div className="absolute bottom-4 left-4 right-4 bg-white/95 backdrop-blur rounded-xl p-4 border border-[hsl(var(--border))]">
                  <div className="label-eyebrow mb-1">Before → After</div>
                  <div className="text-xs text-[hsl(var(--muted-foreground))] font-mono-editor line-through">
                    In this comprehensive analysis, we will delve into…
                  </div>
                  <div className="mt-2 text-sm font-medium">
                    Here's what I actually found when I dug into the numbers.
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Interactive humanizer */}
      <section id="humanizer" className="max-w-7xl mx-auto px-6 lg:px-10 py-14">
        <div className="mb-8 flex items-end justify-between flex-wrap gap-4">
          <div>
            <div className="label-eyebrow mb-3">Try Humanly</div>
            <h2 className="font-display text-4xl sm:text-5xl tracking-tight">Paste. Humanize. Done.</h2>
          </div>
          <p className="text-[hsl(var(--muted-foreground))] max-w-md">
            {user ? (
              <>Signed in on the {user.plan} plan. <Link to="/dashboard" className="link-underline text-[hsl(var(--primary))]">Your dashboard</Link> has your history and saved voices.</>
            ) : (
              <>Anonymous demo: up to 300 words per request. <Link to="/signup" className="link-underline text-[hsl(var(--primary))]">Sign up free</Link> for higher limits and history.</>
            )}
          </p>
        </div>
        <Humanizer compact />
      </section>

      {/* Features / Audiences */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-16">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-10 items-start">
          <div>
            <div className="label-eyebrow mb-3">Who it's for</div>
            <h2 className="font-display text-4xl tracking-tight leading-tight">One tool. <br/>Many voices.</h2>
            <p className="mt-4 text-[hsl(var(--muted-foreground))]">Humanly adapts to the person doing the writing — not the other way around.</p>
          </div>
          <div className="lg:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
            {AUDIENCES.map((a) => (
              <div key={a.title} data-testid={`audience-${a.title.toLowerCase()}`} className="flex gap-4 border-t border-[hsl(var(--border))] pt-5">
                <div className="w-10 h-10 rounded-full bg-[hsl(var(--secondary))] flex items-center justify-center shrink-0 text-[hsl(var(--primary))]">
                  <a.icon size={18} />
                </div>
                <div>
                  <div className="font-semibold">{a.title}</div>
                  <div className="text-sm text-[hsl(var(--muted-foreground))] mt-1">{a.desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="bg-[hsl(var(--muted))]/50 border-y border-[hsl(var(--border))]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20">
          <div className="label-eyebrow mb-3">How it works</div>
          <h2 className="font-display text-4xl sm:text-5xl tracking-tight max-w-2xl leading-tight">Three steps between AI draft and a paragraph that sounds like you.</h2>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-10">
            {HOW.map((step) => (
              <div key={step.n}>
                <div className="font-mono-editor text-sm text-[hsl(var(--accent))]">{step.n}</div>
                <div className="font-display text-2xl mt-2">{step.title}</div>
                <div className="text-[hsl(var(--muted-foreground))] mt-3 leading-relaxed">{step.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing preview */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-20">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
          <div>
            <div className="label-eyebrow mb-3">Pricing</div>
            <h2 className="font-display text-4xl sm:text-5xl tracking-tight">Start free. Scale when you're ready.</h2>
          </div>
          <Link to="/pricing" data-testid="pricing-link" className="link-underline text-[hsl(var(--primary))] font-semibold flex items-center gap-1">
            See full pricing <ArrowRight size={14} />
          </Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[
            { name: "Free", price: "$0", note: "1,500 words / month", cta: "Start free", href: "/signup" },
            { name: "Pro", price: "$12", note: "60,000 words / month", cta: "Choose Pro", href: "/pricing", featured: true },
            { name: "Premium", price: "$29", note: "300,000 words / month", cta: "Choose Premium", href: "/pricing" },
          ].map((t) => (
            <div key={t.name} className={`surface p-8 flex flex-col ${t.featured ? "ring-2 ring-[hsl(var(--primary))]" : ""}`}>
              <div className="font-display text-2xl">{t.name}</div>
              <div className="mt-4 flex items-baseline gap-1">
                <span className="font-display text-5xl">{t.price}</span>
                <span className="text-sm text-[hsl(var(--muted-foreground))]">/mo</span>
              </div>
              <div className="text-sm text-[hsl(var(--muted-foreground))] mt-2">{t.note}</div>
              <Link to={t.href} className={`mt-6 text-center ${t.featured ? "btn-primary" : "btn-ghost"}`}>{t.cta}</Link>
            </div>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section className="max-w-4xl mx-auto px-6 lg:px-10 py-20">
        <div className="label-eyebrow mb-3">FAQ</div>
        <h2 className="font-display text-4xl sm:text-5xl tracking-tight mb-10">Questions, answered.</h2>
        <div className="space-y-5">
          {FAQ.map((f, i) => (
            <details key={i} data-testid={`faq-${i}`} className="surface p-6 group">
              <summary className="cursor-pointer font-semibold flex items-center justify-between list-none">
                <span>{f.q}</span>
                <span className="text-[hsl(var(--accent))] group-open:rotate-45 transition-transform">+</span>
              </summary>
              <div className="mt-3 text-[hsl(var(--muted-foreground))] leading-relaxed">{f.a}</div>
            </details>
          ))}
        </div>
      </section>

      {/* Final CTA */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-20">
        <div className="surface p-10 md:p-16 relative overflow-hidden">
          <div className="absolute -right-20 -bottom-20 w-80 h-80 rounded-full bg-[hsl(var(--accent))]/15 blur-3xl pointer-events-none" />
          <div className="relative">
            <h2 className="font-display text-4xl sm:text-5xl tracking-tight max-w-2xl">Give every word a heartbeat.</h2>
            <p className="mt-4 max-w-xl text-[hsl(var(--muted-foreground))]">Free forever plan. Upgrade whenever you outgrow it.</p>
            <div className="mt-8 flex gap-4">
              <Link to="/signup" data-testid="cta-final-signup" className="btn-primary">Create free account</Link>
              <Link to="/pricing" className="btn-ghost">See pricing</Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
